import { useMemo } from 'react';
import type { ProjectBundle } from '../project.js';

/**
 * PRD 7: left pane is the project tree and git status. PRD 6.1's layout is the shape
 * being rendered — manifests, graphs, agents, schemas, src, web.
 *
 * Files open in Monaco. Before this they were labels, which meant a file with no node
 * pointing at it — a README, a CIVIL.md — could not be reached at all.
 */
export function ProjectTree({
  bundle,
  onOpenFile,
  onInitialize,
  initializing,
  onLiftRepo,
  lifting,
  show = 'all',
  activePath,
}: {
  bundle: ProjectBundle | undefined;
  onOpenFile: (path: string) => void;
  onInitialize: () => void;
  initializing: boolean;
  /** Generate graph from repo (project.liftRepo): read the code into civil/ documents. */
  onLiftRepo: () => void;
  /** A read of the repo is in flight; it can take a while with the model pass. */
  lifting: boolean;
  /**
   * What to draw (prd-deltas §25): the left panel shows only the way to make a
   * repository a Civil project; the File Browser shows only the files.
   */
  show?: 'all' | 'setup' | 'files';
  /** The file open beside the tree, marked in it. */
  activePath?: string | undefined;
}) {
  const grouped = useMemo(() => groupByDirectory(bundle?.files ?? []), [bundle]);

  const pending = useMemo(
    () => new Map((bundle?.pending ?? []).map((c) => [c.path, c.kind])),
    [bundle],
  );

  // A repository with no composition is not a Civil project yet. That is the one
  // state where the tree has something to offer besides a list of files.
  const needsCivil = bundle !== undefined && bundle.composition === undefined;
  if (show === 'setup' && !needsCivil) return null;

  return (
    <>
      <div className="pane-title">{show === 'files' ? 'Files' : 'Project'}</div>
      <div className="pane-body">
        {!bundle ? (
          <p className="muted">No project open.</p>
        ) : (
          <>
            {needsCivil && show !== 'files' ? (
              <div className="add-civil">
                <button type="button" className="add-civil-button" onClick={onInitialize} disabled={initializing}>
                  {initializing ? 'Adding…' : 'Add Civil to this project'}
                </button>
                <p className="add-civil-note">
                  Creates <code>civil.yaml</code>, <code>app.yaml</code> and{' '}
                  <code>CIVIL.md</code> as pending changes. Nothing is committed until
                  you say so.
                </p>
                {/* The other way in, for a repo that already has an app in it: read
                    the code and propose the sketch it implies. */}
                <div className="add-civil-or">or, from the code already here</div>
                <button
                  type="button"
                  className={`add-civil-button is-lift${lifting ? ' is-busy' : ''}`}
                  onClick={onLiftRepo}
                  disabled={lifting || initializing}
                  title="Generate graph from repo (U)"
                >
                  {lifting ? 'Reading the repository…' : 'Generate graph from repo'}
                </button>
                <p className="add-civil-note">
                  {lifting
                    ? 'Reading the NestJS server and Vite client, then naming what it found. This can take a few minutes.'
                    : <>Reads a NestJS server and Vite client and proposes <code>civil/</code>{' '}
                      documents — the app, a graph per service, the registry — as pending
                      changes to review. Nothing is committed.</>}
                </p>
              </div>
            ) : null}

            {show === 'setup' ? null : (
            <div className="tree-list">
              {grouped.map(([dir, files]) => (
                <div key={dir} className="tree-group">
                  <div className="tree-dir">{dir === '' ? '/' : dir}</div>
                  {files.map((file) => {
                    const change = pending.get(file);
                    return (
                      <button
                        key={file}
                        type="button"
                        className={`tree-file${change ? ` is-${change}` : ''}${file === activePath ? ' is-open' : ''}`}
                        title={change ? `${file} — ${change}` : file}
                        onClick={() => onOpenFile(file)}
                      >
                        {change ? <span className="tree-mark">{markFor(change)}</span> : null}
                        {file.split('/').pop()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** PRD 7's git status, as far as it goes before M3 has a diff view. */
function markFor(kind: string): string {
  switch (kind) {
    case 'add': return '+';
    case 'delete': return '−';
    case 'rename': return '→';
    default: return '•';
  }
}

function groupByDirectory(files: string[]): [string, string[]][] {
  const map = new Map<string, string[]>();
  for (const file of files) {
    const slash = file.lastIndexOf('/');
    const dir = slash === -1 ? '' : file.slice(0, slash);
    const list = map.get(dir) ?? [];
    list.push(file);
    map.set(dir, list);
  }
  // Root files first, then directories alphabetically — the manifests are what you
  // look for, and they live at the root.
  return [...map.entries()].sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
}
